import { useMemo } from 'react';
import useSWR from 'swr';

import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { useSelectedEmployeeId } from '~/hooks/employee';

import {
  Competency,
  DevPlan,
  Education,
  getEmpDevCompetencies,
  getEmpDevEducation,
  getEmpDevPlans,
} from './data';

export type UseDevelopmentTabOutput = {
  competenciesData?: Competency;
  educationData?: Education[];
  plansData?: DevPlan;
  competencyEnum: Enum[];
  compPosRelEnum: Enum[];
  compProfEnum: Enum[];
  isLoading: boolean;
};

const useGetEmpDevCompetencies = (employeeId: number) => {
  return useSWR(['empDevCompetencies', employeeId], () =>
    getEmpDevCompetencies(employeeId),
  );
};

const useGetEmpDevEducation = (employeeId: number) => {
  return useSWR(['empDevEducation', employeeId], () =>
    getEmpDevEducation(employeeId),
  );
};

const useGetEmpDevPlans = (employeeId: number) => {
  return useSWR(['empDevPlans', employeeId], () => getEmpDevPlans(employeeId));
};

export const useDevelopmentTab = (): UseDevelopmentTabOutput => {
  const { getEnums } = useAllEnums();
  const enumLists = useMemo(() => {
    return {
      competencyEnum: getEnums(EnumListKey.CORE_COMPETENCY),
      compPosRelEnum: getEnums(EnumListKey.DEV_COMPETENCY_POS_RELATED),
      compProfEnum: getEnums(EnumListKey.DEV_COMPETENCY_PROFESSIONAL),
    };
  }, [getEnums]);

  const { selectedEmployeeId } = useSelectedEmployeeId();

  const { data: competenciesData, isLoading: isLoadingCompetenciesData } =
    useGetEmpDevCompetencies(selectedEmployeeId);
  const { data: educationData, isLoading: isLoadingEducationData } =
    useGetEmpDevEducation(selectedEmployeeId);
  const { data: plansData, isLoading: isLoadingPlansData } =
    useGetEmpDevPlans(selectedEmployeeId);

  const isLoading =
    isLoadingCompetenciesData || isLoadingEducationData || isLoadingPlansData;

  return {
    competenciesData,
    educationData,
    plansData,
    ...enumLists,
    isLoading,
  };
};
